import {
  NOYAU_CODES,
  type NoyauCode,
  type ResumeTraduction,
  resumeTraductionSchema,
} from '@rosetta/shared';
import { z } from 'zod';
import type { ConfigTahiti } from '../config';
import { analyser } from './analyse';
import { TITRE_PAR_DEFAUT } from './documents';
import {
  CODE_LANGUE,
  type Filtre,
  dateTahitiSchema,
  idSchema,
  lireLignes,
  texteLocaliseSchema,
} from './h2data';
import type { AppelHttp } from './http';
import {
  type CorrespondanceStatuts,
  TABLE_TACHE,
  champNoyau,
  classerStatut,
  jamaisDemarree,
} from './taches';

/**
 * The history call: the translations of the signed-in user, read as two H2Data reads
 * (the tasks of the user in the table of the Rosetta v5 tasks, then their source documents)
 * joined by `rose_document_id`.
 * There is no page: one read each, up to `LIMITE_HISTORIQUE` rows (decision of #4, no
 * pagination; the open question is the number beyond which one would be needed).
 *
 * TO VALIDATE(#4): the filter on `h2_user_creator_id` (the field exists on the task and
 * on the document in the live metadata) and the use of the identifier of the login
 * answer as its value; the order of the read, which the client does not rely on (it sorts
 * by date itself); and the limit, whose ceiling in H2Data is not documented here.
 *
 * TO VALIDATE(#6): that the table of the v5 tasks carries the columns this read asks for
 * (`title`, `created_at`, `h2_user_creator_id`, `rose_document_id`, `target_core_*`): the
 * reference script reads other columns of it.
 */
export const LIMITE_HISTORIQUE = 1000;

const CHAMP_CREATEUR = 'h2_user_creator_id';

const booleen = z.boolean().nullish();

export const ligneTacheHistoriqueSchema = z.object({
  id: idSchema,
  title: texteLocaliseSchema.nullish(),
  status: z.string().min(1),
  created_at: dateTahitiSchema,
  /** A task without a document cannot be opened: it is left out, not an error. */
  rose_document_id: idSchema.nullish(),
  target_core_sgp: booleen,
  target_core_spg: booleen,
  target_core_psg: booleen,
  target_core_pgs: booleen,
  target_core_gps: booleen,
  target_core_gsp: booleen,
});
export type LigneTacheHistorique = z.infer<typeof ligneTacheHistoriqueSchema>;

export const ligneDocumentHistoriqueSchema = z.object({
  id: idSchema,
  source_text: texteLocaliseSchema.nullish(),
});

/** Tahiti's own rule, as in `lireTache`: no noyau selected means the six of them. */
function noyauxVises(ligne: LigneTacheHistorique): NoyauCode[] {
  const choisis = NOYAU_CODES.filter(
    (code) => ligne[champNoyau(code) as keyof LigneTacheHistorique] === true,
  );
  return choisis.length > 0 ? choisis : [...NOYAU_CODES];
}

/** Tahiti identifiers are numbers, and so is the filter value when the user id is one. */
function filtreCreateur(utilisateurId: string): Filtre {
  return [CHAMP_CREATEUR, /^\d+$/.test(utilisateurId) ? Number(utilisateurId) : utilisateurId, '='];
}

/**
 * Reads the history of a user: the finished, the running and, since #5, the failed
 * translations, the most recent first. The status is ranked on the task, as `lireTraduction`
 * does before it reads the translations (`classerStatut`, and a task never started is a failure,
 * #13, `jamaisDemarree`). A task whose source document cannot be found is left out: it has
 * neither a text to show nor a result to open.
 */
export async function lireHistorique(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
  utilisateurId: string,
  statuts: CorrespondanceStatuts | undefined,
  maintenant: number,
): Promise<ResumeTraduction[]> {
  const where = filtreCreateur(utilisateurId);
  const [taches, documents] = await Promise.all([
    lireLignes(
      appel,
      config,
      jeton,
      {
        table: TABLE_TACHE,
        fields: [
          'id',
          'title',
          'status',
          'created_at',
          'rose_document_id',
          ...NOYAU_CODES.map(champNoyau),
        ],
        where,
        orderBy: [{ field: 'created_at', direction: 'DESC' }],
        limit: LIMITE_HISTORIQUE,
        lang: CODE_LANGUE,
      },
      ligneTacheHistoriqueSchema,
    ),
    lireLignes(
      appel,
      config,
      jeton,
      {
        table: 'rose_document',
        fields: ['id', 'source_text'],
        where,
        limit: LIMITE_HISTORIQUE,
        lang: CODE_LANGUE,
      },
      ligneDocumentHistoriqueSchema,
    ),
  ]);

  const textes = new Map(documents.map((document) => [document.id, document.source_text]));
  const resumes: ResumeTraduction[] = [];
  for (const tache of taches) {
    const classe = classerStatut(tache.status, statuts);
    const statut =
      classe === 'en_cours' && jamaisDemarree(tache.status, statuts, tache.created_at, maintenant)
        ? 'echouee'
        : classe;
    if (tache.rose_document_id == null) continue;
    const texte = textes.get(tache.rose_document_id);
    if (texte == null) continue;

    const titre = tache.title?.trim();
    resumes.push({
      id: tache.id,
      ...(titre === undefined || titre === '' || titre === TITRE_PAR_DEFAUT ? {} : { titre }),
      texteOrigine: texte,
      noyaux: noyauxVises(tache),
      statut,
      creeLe: tache.created_at,
      // The translations of one document are one card of the history (#11).
      documentId: tache.rose_document_id,
    });
  }

  // The most recent first, whatever the order Tahiti answered in; the identifier, which
  // grows with the creation, settles two tasks of the same instant.
  resumes.sort(
    (a, b) =>
      b.creeLe.localeCompare(a.creeLe) || Number(b.id) - Number(a.id) || b.id.localeCompare(a.id),
  );
  return analyser(z.array(resumeTraductionSchema), resumes, 'history');
}
